import { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import axios from 'axios'
import {
  Calculator, Play, Loader2, CheckCircle, AlertCircle, ChevronDown, Search, Truck, FolderTree
} from 'lucide-react'
import { syncApi } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

interface FactusolSupplier {
  code: string
  name: string
}

interface BatchProgress {
  current: number
  total: number
  status: 'idle' | 'running' | 'completed' | 'error'
  message: string
  updated: number
  skipped: number
  errors: number
}

interface BatchStatus {
  is_running: boolean
  progress: BatchProgress
}

interface SingleReferenceResult {
  success: boolean
  reference: string
  family_code: string
  cost_price: number
  calculated_prices: {
    pvp: number
    pvd: number
    vip: number
  }
  price_details: {
    pvp: { margin: number; base_price: number; final_net_price: number; final_with_iva: number }
    pvd: { margin: number; base_price: number; final_net_price: number; final_with_iva: number }
    vip: { margin: number; base_price: number; final_net_price: number; final_with_iva: number }
  }
  message: string
}

interface SearchProduct {
  codart: string
  refart: string
  name: string
  family: string
  cost_price: number
}

type BatchMode = 'family' | 'supplier'

export default function CalculatePricesPage() {
  const toast = useToast()
  const [batchMode, setBatchMode] = useState<BatchMode>('family')
  const [selectedFamily, setSelectedFamily] = useState<string>('')
  const [selectedSupplier, setSelectedSupplier] = useState<string>('')
  const [pollingEnabled, setPollingEnabled] = useState(false)
  const [singleReference, setSingleReference] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchProduct[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [singleResult, setSingleResult] = useState<SingleReferenceResult | null>(null)

  // Fetch families
  const familiesQuery = useQuery({
    queryKey: ['families-for-pricing'],
    queryFn: () => syncApi.getFamilies()
  })

  // Fetch suppliers
  const suppliersQuery = useQuery({
    queryKey: ['factusol-suppliers-for-pricing'],
    queryFn: async () => {
      const res = await axios.get('/api/product-ai/factusol/suppliers')
      return (res.data.suppliers || []).map((s: any) => ({
        code: String(s.CODPRO),
        name: s.NOFPRO || s.NOCPRO || `Proveedor ${s.CODPRO}`
      })) as FactusolSupplier[]
    }
  })

  // Fetch calculation status
  const statusQuery = useQuery({
    queryKey: ['batch-calculate-status'],
    queryFn: async () => {
      const res = await axios.get('/api/pricing/batch-calculate/status')
      return res.data as BatchStatus
    },
    refetchInterval: pollingEnabled ? 1000 : false
  })

  // Start/stop polling based on status
  useEffect(() => {
    if (statusQuery.data?.is_running) {
      setPollingEnabled(true)
    } else if (statusQuery.data?.progress.status === 'completed' || statusQuery.data?.progress.status === 'error') {
      setPollingEnabled(false)
    }
  }, [statusQuery.data])

  // Start calculation mutation (by family)
  const calculateFamilyMutation = useMutation({
    mutationFn: async (familyCode: string | null) => {
      const res = await axios.post('/api/pricing/batch-calculate', {
        family_code: familyCode || null
      })
      return res.data
    },
    onSuccess: () => {
      setPollingEnabled(true)
      statusQuery.refetch()
    }
  })

  // Start calculation mutation (by supplier)
  const calculateSupplierMutation = useMutation({
    mutationFn: async (supplierCode: string) => {
      const res = await axios.post('/api/pricing/batch-calculate-supplier', {
        supplier_code: supplierCode
      })
      return res.data
    },
    onSuccess: () => {
      setPollingEnabled(true)
      statusQuery.refetch()
    }
  })

  // Single reference calculation mutation
  const singleReferenceMutation = useMutation({
    mutationFn: async (reference: string) => {
      const res = await axios.post('/api/pricing/calculate-single-reference', { reference })
      return res.data as SingleReferenceResult
    },
    onSuccess: (data) => {
      setSingleResult(data)
    },
    onError: (error: any) => {
      setSingleResult(null)
      toast.error('Error al calcular precio', error.response?.data?.detail || 'Error desconocido')
    }
  })

  const handleCalculate = () => {
    if (batchMode === 'family') {
      const familyName = selectedFamily
        ? familiesQuery.data?.families?.find((f) => f.code === selectedFamily)?.name || selectedFamily
        : 'todas las familias'

      const message = selectedFamily
        ? `¿Recalcular precios de todos los productos de la familia "${familyName}" desde su precio de coste?\n\nEsto actualizará las tarifas en Factusol según los márgenes configurados y las excepciones de precio.`
        : `¿Recalcular precios de TODOS los productos desde su precio de coste?\n\nEsto actualizará las tarifas en Factusol según los márgenes configurados y las excepciones de precio.\n\nEsta operación puede tardar varios minutos.`

      if (confirm(message)) {
        calculateFamilyMutation.mutate(selectedFamily || null)
      }
    } else {
      if (!selectedSupplier) {
        toast.error('Error', 'Selecciona un proveedor')
        return
      }

      const supplierName = suppliersQuery.data?.find((s) => s.code === selectedSupplier)?.name || selectedSupplier

      const message = `¿Recalcular precios de todos los productos del proveedor "${supplierName}" desde su precio de coste?\n\nEsto actualizará las tarifas en Factusol según los márgenes configurados.`

      if (confirm(message)) {
        calculateSupplierMutation.mutate(selectedSupplier)
      }
    }
  }

  // Search products mutation
  const searchProductsMutation = useMutation({
    mutationFn: async (query: string) => {
      const res = await axios.get(`/api/pricing/search-product?q=${encodeURIComponent(query)}`)
      return res.data.products as SearchProduct[]
    },
    onSuccess: (data) => {
      setSearchResults(data)
      setShowDropdown(data.length > 0)
    }
  })

  // Debounced search
  useEffect(() => {
    if (searchQuery.length >= 2) {
      const timer = setTimeout(() => {
        searchProductsMutation.mutate(searchQuery)
      }, 300)
      return () => clearTimeout(timer)
    } else {
      setSearchResults([])
      setShowDropdown(false)
    }
  }, [searchQuery])

  const handleSelectProduct = (product: SearchProduct) => {
    setSingleReference(product.codart)
    setSearchQuery(`${product.codart} - ${product.name}`)
    setShowDropdown(false)
    setSingleResult(null)
  }

  const handleSingleReferenceCalculate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!singleReference.trim()) return
    setSingleResult(null)
    setShowDropdown(false)
    singleReferenceMutation.mutate(singleReference.trim())
  }

  const isRunning = statusQuery.data?.is_running || calculateFamilyMutation.isPending || calculateSupplierMutation.isPending
  const progress = statusQuery.data?.progress

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="p-2 bg-success-100 dark:bg-green-900 rounded-lg">
          <Calculator className="w-8 h-8 text-success-600 dark:text-success-400" />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Calcular Precios desde Coste
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Recalcula los precios de venta a partir del precio de coste
          </p>
        </div>
      </div>

      {/* Single Reference Card */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Calcular una referencia
        </h3>
        <form onSubmit={handleSingleReferenceCalculate} className="space-y-4">
          <div className="flex gap-3">
            <div className="relative flex-1 max-w-lg">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  // Si el usuario escribe algo nuevo, limpiar la referencia seleccionada
                  if (singleReference && e.target.value !== `${singleReference} - `) {
                    setSingleReference('')
                  }
                }}
                onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                placeholder="Buscar por referencia o nombre de producto..."
                className="w-full pl-10 pr-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-green-500"
              />
              {searchProductsMutation.isPending && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 animate-spin" />
              )}

              {/* Dropdown de resultados */}
              {showDropdown && searchResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                  {searchResults.map((product) => (
                    <button
                      key={product.codart}
                      type="button"
                      onClick={() => handleSelectProduct(product)}
                      className="w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 dark:text-white truncate">
                            {product.codart}
                            {product.refart && product.refart !== product.codart && (
                              <span className="text-slate-500 dark:text-slate-400 ml-2">({product.refart})</span>
                            )}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{product.name}</p>
                        </div>
                        <div className="text-right ml-3">
                          <p className="text-sm font-medium text-success-600 dark:text-success-400">
                            {product.cost_price.toFixed(2)} €
                          </p>
                          <p className="text-xs text-slate-400">{product.family}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={singleReferenceMutation.isPending || !singleReference.trim()}
              className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {singleReferenceMutation.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Calculator className="w-5 h-5" />
              )}
              Calcular
            </button>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Escribe al menos 2 caracteres para buscar por código o nombre de producto
          </p>

          {/* Single Reference Result */}
          {singleResult && (
            <div className={`border rounded-xl p-4 ${singleResult.success ? 'border-green-200 dark:border-green-800 bg-success-50 dark:bg-success-900/20' : 'border-red-200 dark:border-red-800 bg-danger-50 dark:bg-danger-900/20'}`}>
              <div className="flex items-center gap-2 mb-3">
                {singleResult.success ? (
                  <CheckCircle className="w-5 h-5 text-success-600 dark:text-success-400" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-danger-600 dark:text-danger-400" />
                )}
                <span className={`font-medium ${singleResult.success ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
                  {singleResult.message}
                </span>
              </div>

              {singleResult.success && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500 dark:text-slate-400">Referencia</p>
                    <p className="font-semibold text-slate-900 dark:text-white">{singleResult.reference}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 dark:text-slate-400">Familia</p>
                    <p className="font-semibold text-slate-900 dark:text-white">{singleResult.family_code || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 dark:text-slate-400">Precio Coste</p>
                    <p className="font-semibold text-slate-900 dark:text-white">{singleResult.cost_price.toFixed(2)} €</p>
                  </div>
                  <div>
                    <p className="text-slate-500 dark:text-slate-400">Margen PVP</p>
                    <p className="font-semibold text-slate-900 dark:text-white">{singleResult.price_details.pvp.margin}%</p>
                  </div>
                </div>
              )}

              {singleResult.success && (
                <div className="mt-4 pt-4 border-t border-green-200 dark:border-green-800">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Precios calculados (neto / con IVA)</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-3 text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400">PVP</p>
                      <p className="font-bold text-lg text-success-600 dark:text-success-400">
                        {singleResult.price_details.pvp.final_net_price.toFixed(2)} €
                      </p>
                      <p className="text-xs text-slate-400">
                        {singleResult.price_details.pvp.final_with_iva.toFixed(2)} € IVA inc.
                      </p>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-3 text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400">PVD</p>
                      <p className="font-bold text-lg text-primary-600 dark:text-primary-400">
                        {singleResult.price_details.pvd.final_net_price.toFixed(2)} €
                      </p>
                      <p className="text-xs text-slate-400">
                        {singleResult.price_details.pvd.final_with_iva.toFixed(2)} € IVA inc.
                      </p>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-3 text-center">
                      <p className="text-xs text-slate-500 dark:text-slate-400">VIP</p>
                      <p className="font-bold text-lg text-purple-600 dark:text-purple-400">
                        {singleResult.price_details.vip.final_net_price.toFixed(2)} €
                      </p>
                      <p className="text-xs text-slate-400">
                        {singleResult.price_details.vip.final_with_iva.toFixed(2)} € IVA inc.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </form>
      </div>

      {/* Main Card */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Cálculo masivo
        </h3>
        <div className="space-y-6">
          {/* Description */}
          <div className="bg-primary-50 dark:bg-primary-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">¿Cómo funciona?</h4>
            <div className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
              <p>1. Obtiene el <strong>precio de coste</strong> de cada producto desde Factusol</p>
              <p>2. Aplica los <strong>márgenes de precio</strong> configurados (proveedor &gt; familia &gt; global)</p>
              <p>3. Si existe una <strong>excepción de precio</strong> para el producto, usa esos márgenes</p>
              <p>4. Actualiza las <strong>tarifas (PVP, PVD, VIP)</strong> en Factusol</p>
              <p>5. La <strong>sincronización</strong> posterior reflejará los cambios en PrestaShop</p>
            </div>
          </div>

          {/* Mode Selector Tabs */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Calcular por
            </label>
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setBatchMode('family')}
                disabled={isRunning}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  batchMode === 'family'
                    ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                } disabled:opacity-50`}
              >
                <FolderTree className="w-4 h-4" />
                Por Familia
              </button>
              <button
                onClick={() => setBatchMode('supplier')}
                disabled={isRunning}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  batchMode === 'supplier'
                    ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                } disabled:opacity-50`}
              >
                <Truck className="w-4 h-4" />
                Por Proveedor
              </button>
            </div>
          </div>

          {/* Family Selector */}
          {batchMode === 'family' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Familia de productos
              </label>
              <div className="relative max-w-md">
                <select
                  value={selectedFamily}
                  onChange={(e) => setSelectedFamily(e.target.value)}
                  disabled={isRunning}
                  className="appearance-none w-full pl-4 pr-10 py-3 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                >
                  <option value="">Todas las familias</option>
                  {familiesQuery.data?.families?.map((family) => (
                    <option key={family.code} value={family.code}>
                      {family.code} - {family.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Selecciona una familia específica o deja "Todas las familias" para procesar todos los productos.
              </p>
            </div>
          )}

          {/* Supplier Selector */}
          {batchMode === 'supplier' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Proveedor
              </label>
              <div className="relative max-w-md">
                <select
                  value={selectedSupplier}
                  onChange={(e) => setSelectedSupplier(e.target.value)}
                  disabled={isRunning || suppliersQuery.isLoading}
                  className="appearance-none w-full pl-4 pr-10 py-3 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                >
                  <option value="">-- Seleccionar proveedor --</option>
                  {suppliersQuery.data?.map((supplier) => (
                    <option key={supplier.code} value={supplier.code}>
                      {supplier.code} - {supplier.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Selecciona un proveedor para calcular precios de todos sus productos.
              </p>
            </div>
          )}

          {/* Progress Display */}
          {progress && progress.status !== 'idle' && (
            <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {progress.status === 'running' ? 'Procesando...' :
                   progress.status === 'completed' ? 'Completado' :
                   progress.status === 'error' ? 'Error' : ''}
                </span>
                {progress.status === 'running' && (
                  <span className="text-sm text-slate-500">
                    {progress.current} / {progress.total}
                  </span>
                )}
              </div>

              {progress.total > 0 && (
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      progress.status === 'error' ? 'bg-danger-500' :
                      progress.status === 'completed' ? 'bg-success-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  />
                </div>
              )}

              <p className="text-sm text-slate-600 dark:text-slate-400">
                {progress.message}
              </p>

              {(progress.status === 'completed' || progress.status === 'error') && (
                <div className="grid grid-cols-3 gap-4 pt-2 border-t border-slate-200 dark:border-slate-700">
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Actualizados</p>
                    <p className="text-lg font-semibold text-success-600 dark:text-success-400">
                      {progress.updated}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Omitidos</p>
                    <p className="text-lg font-semibold text-slate-600 dark:text-slate-400">
                      {progress.skipped}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Errores</p>
                    <p className="text-lg font-semibold text-danger-600 dark:text-danger-400">
                      {progress.errors}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Button */}
          <div className="flex items-center gap-4">
            <button
              onClick={handleCalculate}
              disabled={isRunning}
              className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isRunning ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Calculando...
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  Calcular Precios
                </>
              )}
            </button>

            {!isRunning && progress?.status === 'completed' && (
              <div className="flex items-center gap-2 text-success-600 dark:text-success-400">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm font-medium">Último cálculo completado</span>
              </div>
            )}

            {!isRunning && progress?.status === 'error' && (
              <div className="flex items-center gap-2 text-danger-600 dark:text-danger-400">
                <AlertCircle className="w-5 h-5" />
                <span className="text-sm font-medium">Error en el último cálculo</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
        <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">Nota importante</h4>
        <div className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
          <p>• Los productos sin precio de coste en Factusol serán omitidos.</p>
          <p>• Se actualizan las 3 tarifas: PVP (tarifa 1), PVD (tarifa 2) y VIP (tarifa 3).</p>
          <p>• Después de completar, ejecuta la <strong>sincronización</strong> para reflejar los precios en PrestaShop.</p>
        </div>
      </div>
    </div>
  )
}
